import {
  getAPICallAuthHeader,
  handleSessionRevoked,
  isAuthReady,
  isSessionRevoked,
} from "./backendService";
import utils from "../utils";
import keys from "../configs/constants";
import moment from "moment";

const callAuditApi = async (method, endpoint, body) => {
  const url = `${keys.AUDIT_API_URL}/${endpoint}`;
  const request = {};

  request.method = method;
  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return;

  config = {
    ...config,
    headers: {
      ...config.headers,
      "Content-Type": "application/json; charset=utf-8",
      "X-Origin": "CEM WebUI", // Set the origin header
    },
  };

  request.headers = config.headers;
  request.body = JSON.stringify({
    occurredAt: moment().toISOString(true),
    origin: {
      application: "CEM WebUI",
      user_agent: navigator.userAgent,
    },
    ...body,
  });

  try {
    const response = await fetch(url, request);
    if (response.status === 401 && isSessionRevoked(response)) {
      handleSessionRevoked();
    }
  } catch (error) {
    utils.debug("Audit API call failed", error);
  }
};

/**
 * Reads the newest audit events matching the filters, for the Audit log table.
 * Takes the same body as exportAuditLog, so the table and the export always
 * agree. Resolves to { total, limit, truncated, fields, events }, or to null
 * while sign-in is not ready; rejects with the status and the server's message.
 */
export const queryAuditLog = async (filters) => {
  const config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;

  const response = await fetch(`${keys.AUDIT_API_URL}/query`, {
    method: "POST",
    headers: {
      ...config.headers,
      "Content-Type": "application/json; charset=utf-8",
      "X-Origin": "CEM WebUI",
    },
    body: JSON.stringify(filters),
  });

  if (response.status === 401 && isSessionRevoked(response)) {
    handleSessionRevoked();
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const error = new Error(
      body?.error || `Failed to query audit log (${response.status} ${response.statusText || ""})`
    );
    error.status = response.status;
    throw error;
  }

  return response.json();
};

export const exportAuditLog = async (exportParams = {}) => {
  const endpoint = "export";
  const url = `${keys.AUDIT_API_URL}/${endpoint}`;

  let config = await getAPICallAuthHeader();

  if (!isAuthReady(config)) {
    return null;
  }

  const headers = {
    ...config.headers,
    "Content-Type": "application/json; charset=utf-8",
    Accept: "application/vnd.ms-excel, application/octet-stream",
    "X-Origin": "CEM WebUI",
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(exportParams ?? {}),
  });

  if (response.status === 401 && isSessionRevoked(response)) {
    handleSessionRevoked();
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => "");
    const error = new Error(
      `Failed to export audit log (${response.status} ${response.statusText || ""})`
    );
    error.status = response.status;
    error.body = errorBody;
    throw error;
  }

  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") || "";

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const asciiMatch = /filename="?([^";]+)"?/i.exec(disposition);

  let fileName = null;

  if (utf8Match?.[1]) {
    try {
      fileName = decodeURIComponent(utf8Match[1]);
    } catch (err) {
      utils.debug("error", "Unable to decode UTF-8 filename", err);
    }
  }

  if (!fileName && asciiMatch?.[1]) {
    fileName = asciiMatch[1];
  }

  if (!fileName) {
    const timestamp = new Date().toISOString().replaceAll(/[:.]/g, "-");
    fileName = `audit-log-export-${timestamp}.xls`;
  }

  return {
    blob,
    fileName,
  };
};

/**
 * Sends a 'CommandSent' audit event for a controller.
 * @param eventData
 */
export async function sendControllerCommandSent(eventData) {
  const {
    code,
    status,
    oldValue,
    newValue,
    organization,
    site,
    fleet,
    asset,
    gateway,
    controller,
  } = eventData;

  const payload = {
    eventType: "Controller.CommandSent",
    status,
    organization,
    site,
    fleet: fleet ?? null,
    asset: asset ?? null,
    gateway,
    controller,
    eventDetail: {
      code,
      oldValue,
      newValue,
    },
  };
  return callAuditApi("POST", "send", payload);
}

/**
 * Sends a 'ParameterChanged' audit event for a controller.
 * @param eventData
 * @param isDongle
 *
 */
export async function changeParameterEvent(eventData, isDongle = false) {
  const { status, organization, site, gateway, controller, parameters, fleet, asset } =
    eventData;

  const payload = {
    eventType: "Controller.ParametersChanged",
    version: isDongle ? 1 : 2,
    status,
    organization,
    site,
    gateway,
    controller,
    eventDetail: {
      parameters,
    },
    fleet: fleet ?? null,
    asset: asset ?? null,
  };
  return callAuditApi("POST", "send", payload);
}

/**
 * Sends a 'Controller.DetailViewed' audit event when user accesses the controller view from fleet page.
 * @param eventData
 * 
 */
export async function sendControllerDetailViewed(eventData) {
  const { organization, site, controller, fleet, asset, gateway } = eventData;

  const payload = {
    eventType: "Controller.DetailViewed",
    organization,
    site,
    controller,
    fleet,
    asset,
    gateway,
  };
  return callAuditApi("POST", "send", payload);
}
